import React, { useState, useEffect } from 'react';
import { X, Key, Plus, Trash2, Copy, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { generateApiKey, getApiKeys, revokeApiKey } from '../lib/api';

interface ApiKeyManagerProps {
  onClose: () => void;
}

interface ApiKey {
  key: string;
  created: string;
  active: boolean;
  lastUsed: string | null;
}

export const ApiKeyManager: React.FC<ApiKeyManagerProps> = ({ onClose }) => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({});
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [newKeyGenerated, setNewKeyGenerated] = useState<string | null>(null);

  // Load API keys
  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = () => {
    const keys = getApiKeys();
    setApiKeys(keys);
    
    // Initialize visibility state for each key
    const initialVisibility: Record<string, boolean> = {};
    keys.forEach(key => {
      initialVisibility[key.key] = false;
    });
    setShowKeys(initialVisibility);
  };

  const handleGenerateKey = () => {
    const newKey = generateApiKey();
    setNewKeyGenerated(newKey);
    loadApiKeys();
  };

  const handleRevokeKey = (key: string) => {
    if (window.confirm('Möchten Sie diesen API-Schlüssel wirklich widerrufen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      revokeApiKey(key);
      loadApiKeys();
    }
  };

  const toggleKeyVisibility = (key: string) => {
    setShowKeys(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const copyToClipboard = (key: string) => {
    navigator.clipboard.writeText(key).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Nie';
    return new Date(dateString).toLocaleString('de-DE');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Key className="w-6 h-6 text-blue-600 shrink-0" />
            <span className="break-words">API-Schlüssel verwalten</span>
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors shrink-0"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Info box */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-800 mb-2">API-Zugriff</h3>
            <p className="text-sm text-blue-700 mb-2">
              API-Schlüssel ermöglichen den Zugriff auf die InstallTrack Pro API. Verwenden Sie diese Schlüssel, um externe Anwendungen zu integrieren.
            </p>
            <p className="text-sm text-blue-700">
              <strong>Sicherheitshinweis:</strong> Behandeln Sie API-Schlüssel wie Passwörter. Teilen Sie sie nicht öffentlich und speichern Sie sie sicher.
            </p>
          </div>

          {/* New key generated notification */}
          {newKeyGenerated && (
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">Neuer API-Schlüssel generiert</h3>
                  <p className="text-sm text-green-700 mt-1">
                    Kopieren Sie diesen Schlüssel jetzt. Er wird aus Sicherheitsgründen nicht wieder vollständig angezeigt.
                  </p>
                  <div className="mt-2 flex items-center gap-2 bg-white p-2 rounded border border-green-200">
                    <code className="text-sm font-mono text-green-800 flex-1 break-all">
                      {newKeyGenerated}
                    </code>
                    <button
                      onClick={() => copyToClipboard(newKeyGenerated)}
                      className="p-1 text-green-700 hover:text-green-900 hover:bg-green-100 rounded"
                      title="In die Zwischenablage kopieren"
                    >
                      {copiedKey === newKeyGenerated ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <Copy className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Generate new key button */}
          <div className="flex justify-end">
            <button
              onClick={handleGenerateKey}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Neuen API-Schlüssel generieren</span>
            </button>
          </div>

          {/* API keys list */}
          <div className="border rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    API-Schlüssel
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Erstellt am
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Zuletzt verwendet
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Aktionen</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {apiKeys.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                      Keine API-Schlüssel vorhanden
                    </td>
                  </tr>
                ) : (
                  apiKeys.map((apiKey) => (
                    <tr key={apiKey.key} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <Key className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="ml-4 flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <code className="text-sm font-mono text-gray-900 truncate">
                                {showKeys[apiKey.key] 
                                  ? apiKey.key 
                                  : `${apiKey.key.substring(0, 8)}...${apiKey.key.substring(apiKey.key.length - 4)}`}
                              </code>
                              <button
                                onClick={() => toggleKeyVisibility(apiKey.key)}
                                className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                                title={showKeys[apiKey.key] ? "Schlüssel verbergen" : "Schlüssel anzeigen"}
                              >
                                {showKeys[apiKey.key] ? (
                                  <EyeOff className="w-4 h-4" />
                                ) : (
                                  <Eye className="w-4 h-4" />
                                )}
                              </button>
                              <button
                                onClick={() => copyToClipboard(apiKey.key)}
                                className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                                title="In die Zwischenablage kopieren"
                              >
                                {copiedKey === apiKey.key ? (
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                ) : (
                                  <Copy className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(apiKey.created)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(apiKey.lastUsed)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          apiKey.active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {apiKey.active ? 'Aktiv' : 'Widerrufen'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {apiKey.active && (
                          <button
                            onClick={() => handleRevokeKey(apiKey.key)}
                            className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50"
                            title="API-Schlüssel widerrufen"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* API documentation link */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">API-Dokumentation</h3>
            <p className="text-sm text-gray-600 mb-2">
              Die API-Dokumentation enthält alle Informationen, die Sie für die Integration mit InstallTrack Pro benötigen.
            </p>
            <button 
              className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
              onClick={() => {
                onClose();
                // Open API documentation
                document.dispatchEvent(new CustomEvent('open-api-documentation'));
              }}
            >
              <span>API-Dokumentation öffnen</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </button>
          </div>

          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};